import React, { ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '@shared/ui/organisms/layout/Layout';
import { OnboardingScreen, selectActiveScreen } from '../../store/onboarding.duck';
import OnboardTeamMember from './components/OnboardTeamMember';
import OnboardingComplete from './components/OnboardingComplete';
import OnboardingCreateTeam from './components/OnboardingCreateTeam';
import './onboarding.scss';
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { setUser } from "@shared/store/auth.duck";
import OnboardingCreateOffice from './components/OnboardingCreateOffice';
import SetupHolidayRules from './components/SetupHolidayRules';
import SetupHolidayAllowance from './components/SetupHolidayAllowance';
import styled from "styled-components";
import { Auth } from "@shared/services/Auth";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import OnboardingOfficeCreated from "./components/OnboardingOfficeCreated";
import OnboardingWelcome from "./components/OnboardingWelcome";
import OnboardingFeatures from "./components/OnboardingFeatures";
import OnboardingCustomWhereabouts from "./components/OnboardingCustomWhereabouts";
import OnboardingIcons from "./components/OnboardingIcons";

export default function OnboardingPage() {
  return (
    <Layout style={{paddingTop: 48}}>
      <OnboardingContent/>
    </Layout>
  );
}

function renderScreen(screen: OnboardingScreen): ReactNode {
  switch (screen) {
    case OnboardingScreen.Welcome:
      return <OnboardingWelcome/>;
    case OnboardingScreen.Features:
      return <OnboardingFeatures/>;
    case OnboardingScreen.CreateTeam:
      return <OnboardingCreateTeam/>;
    case OnboardingScreen.SetupOffice:
      return <OnboardingCreateOffice/>;
    case OnboardingScreen.OfficeAdded:
      return <OnboardingOfficeCreated/>;
    case OnboardingScreen.AddMember:
      return <OnboardTeamMember/>;
    case OnboardingScreen.SetupHolidayAllowance:
      return <SetupHolidayAllowance/>;
    case OnboardingScreen.SetupHolidayRules:
      return <SetupHolidayRules/>;
    case OnboardingScreen.CustomWhereabouts:
      return <OnboardingCustomWhereabouts/>;
    case OnboardingScreen.Icons:
      return <OnboardingIcons/>;
    case OnboardingScreen.Complete:
      return <OnboardingComplete/>;
    default:
      return null;
  }
}

function OnboardingContent() {
  const activeScreen = useSelector(selectActiveScreen);
  const dispatch = useDispatch();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (activeScreen === OnboardingScreen.Complete) {
      Auth.current(false).then((user) => dispatch(setUser(user)))
    }
  }, [dispatch, activeScreen]);

  // Only the active step is mounted; AnimatePresence "wait" plays the
  // outgoing step's exit before the incoming step's enter so the two never
  // overlap or fight for layout space between transitions.
  const variants = reduceMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.15 } },
        exit: { opacity: 0, transition: { duration: 0.1 } },
      }
    : {
        initial: { opacity: 0, y: 24 },
        animate: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
        exit: { opacity: 0, y: -12, transition: { duration: 0.18, ease: 'easeIn' } },
      };

  return (
    <OnboardingWrapper data-test="onboarding">
      <AnimatePresence mode="wait">
        <motion.div
          key={activeScreen}
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
        >
          {renderScreen(activeScreen)}
        </motion.div>
      </AnimatePresence>
    </OnboardingWrapper>
  )
}

const OnboardingWrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  @media (${tablet}) {
    max-width: 480px;
  }
`;

